import { useState } from "react";
import { Play } from "lucide-react";

interface VimeoEmbedProps {
  videoId?: string;
  // Unlisted-Videos von Vimeo brauchen ihren geheimen Zusatz (?h=...), sonst spielt der Player sie nicht ab.
  hash?: string;
  title: string;
  posterUrl?: string;
  aspect?: string;
}

function hasUsableVideoId(videoId?: string) {
  return !!videoId && !videoId.toLowerCase().startsWith("todo");
}

export function VimeoEmbed({
  videoId,
  hash,
  title,
  posterUrl,
  aspect = "16 / 9",
}: VimeoEmbedProps) {
  const [loaded, setLoaded] = useState(false);
  const canLoad = hasUsableVideoId(videoId);

  return (
    <div
      className="relative overflow-hidden rounded-md border border-border bg-muted"
      style={{ aspectRatio: aspect }}
    >
      {loaded && canLoad ? (
        <iframe
          src={`https://player.vimeo.com/video/${videoId}?autoplay=1&dnt=1${hash ? `&h=${hash}` : ""}`}
          title={title}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <>
          <button
            type="button"
            onClick={() => canLoad && setLoaded(true)}
            disabled={!canLoad}
            className="group absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-6 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed"
            aria-label={canLoad ? `${title} laden` : `${title} folgt`}
          >
            {posterUrl ? (
              <img
                src={posterUrl}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            ) : null}
            <span className="absolute inset-0 bg-primary/45" aria-hidden="true" />
            <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand-button text-brand-button-foreground transition-transform group-enabled:group-hover:scale-105">
              <Play className="h-7 w-7 fill-current" strokeWidth={1.75} />
            </span>
            <span className="relative max-w-sm font-body text-sm leading-relaxed text-primary-foreground md:text-base">
              {canLoad
                ? "Klick zum Laden des Videos. Dabei werden Daten an Vimeo übertragen."
                : "Video folgt."}
            </span>
          </button>
          {canLoad ? (
            <a
              href="/datenschutz"
              className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 font-display text-xs font-extrabold text-primary-foreground underline decoration-primary-foreground/60 underline-offset-4"
            >
              Datenschutzerklärung
            </a>
          ) : null}
        </>
      )}
    </div>
  );
}